/**
 * Phase 15 — Admin Automated Recommended Follow-Up Actions page.
 *
 * Route: /admin/partners/follow-ups (admin only)
 *
 * Surfaces:
 *   1. Stat strip — totals by severity + status across the window
 *   2. Filter bar — partner_id / region / category / severity / status
 *   3. Follow-Up Action Queue — one row per (booking, category)
 *      with admin status select + booking + passport deep-links
 *
 * SAFETY (pinned):
 *   • Recommendation-only. NO emails / invites / SMS / work-orders /
 *     quotes / ServiceM8 / commission changes.
 *   • Status mutations are admin-only and audit-logged.
 *   • Calm wording: "recommended review", "potential follow-up",
 *     "requires review against ADWG guidance". No medical claims.
 *   • BioCleanse is supplementary only; surfaced as italic note when
 *     attached to microbiological / turbidity recommendations.
 *   • Soda Ash / Reverse Osmosis never appear as default actions.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, RefreshCw, AlertTriangle, Loader2, ShieldAlert,
  ListChecks, Filter, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PortalShell } from "@/components/PortalShell";

const WINDOWS = [
  { id: "all",        label: "All time" },
  { id: "this_month", label: "This month" },
  { id: "last_30d",   label: "Last 30d" },
  { id: "last_7d",    label: "Last 7d" },
];

const STATUSES = [
  "new",
  "reviewed",
  "manually_assigned",
  "partner_contacted_manually",
  "customer_contacted_manually",
  "quoted",
  "completed",
  "dismissed",
  "not_applicable",
];

const SEVERITIES = ["high", "medium", "low", "routine"];

const STATUS_TONES = {
  new:                          "bg-cyan-100 text-cyan-800",
  reviewed:                     "bg-violet-100 text-violet-800",
  manually_assigned:            "bg-indigo-100 text-indigo-800",
  partner_contacted_manually:   "bg-amber-100 text-amber-800",
  customer_contacted_manually:  "bg-amber-100 text-amber-900",
  quoted:                       "bg-blue-100 text-blue-800",
  completed:                    "bg-emerald-100 text-emerald-800",
  dismissed:                    "bg-slate-200 text-slate-700",
  not_applicable:               "bg-slate-100 text-slate-500",
};

const SEVERITY_TONES = {
  high:    "bg-rose-100 text-rose-800",
  medium:  "bg-amber-100 text-amber-800",
  low:     "bg-slate-100 text-slate-700",
  routine: "bg-slate-100 text-slate-500",
};

const fmtNum = (n) => Number(n || 0).toLocaleString();

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-AU"); }
  catch { return iso; }
};

const prettyStatus = (s) => (s || "").replaceAll("_", " ");


// ── Window selector ─────────────────────────────────────────────────────
function WindowSelector({ window, onChange }) {
  return (
    <div
      className="inline-flex rounded-full bg-slate-100 p-1 text-xs font-semibold"
      data-testid="ph15-window-selector"
    >
      {WINDOWS.map((w) => (
        <button
          key={w.id}
          onClick={() => onChange(w.id)}
          data-testid={`ph15-window-${w.id}`}
          className={`px-3 py-1 rounded-full transition ${
            window === w.id
              ? "bg-violet-600 text-white shadow-sm"
              : "text-slate-600 hover:text-slate-800"
          }`}
        >
          {w.label}
        </button>
      ))}
    </div>
  );
}


// ── Stat strip ─────────────────────────────────────────────────────────
function StatStrip({ summary }) {
  const total = summary?.total ?? 0;
  const high = summary?.by_severity?.high ?? 0;
  const reviewed =
    (summary?.by_status?.reviewed ?? 0) +
    (summary?.by_status?.manually_assigned ?? 0) +
    (summary?.by_status?.partner_contacted_manually ?? 0) +
    (summary?.by_status?.customer_contacted_manually ?? 0) +
    (summary?.by_status?.quoted ?? 0) +
    (summary?.by_status?.completed ?? 0);
  const newCount = summary?.by_status?.new ?? 0;
  return (
    <div
      data-testid="ph15-stat-strip"
      className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3"
    >
      <div
        data-testid="ph15-stat-total"
        className="rounded-lg p-3 bg-white border border-slate-200"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          Total follow-ups
        </div>
        <div className="mt-1 text-lg font-bold">{fmtNum(total)}</div>
      </div>
      <div
        data-testid="ph15-stat-new"
        className="rounded-lg p-3 bg-cyan-50 border border-cyan-100"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">
          New
        </div>
        <div className="mt-1 text-lg font-bold text-cyan-900">
          {fmtNum(newCount)}
        </div>
      </div>
      <div
        data-testid="ph15-stat-high"
        className="rounded-lg p-3 bg-rose-50 border border-rose-100"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-rose-700">
          High priority
        </div>
        <div className="mt-1 text-lg font-bold text-rose-900">
          {fmtNum(high)}
        </div>
      </div>
      <div
        data-testid="ph15-stat-actioned"
        className="rounded-lg p-3 bg-emerald-50 border border-emerald-100"
      >
        <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">
          Reviewed / actioned
        </div>
        <div className="mt-1 text-lg font-bold text-emerald-900">
          {fmtNum(reviewed)}
        </div>
      </div>
    </div>
  );
}


// ── Filter bar ─────────────────────────────────────────────────────────
function FilterBar({
  partners,
  regions,
  categories,
  partnerId,
  region,
  category,
  severity,
  status,
  onPartnerId,
  onRegion,
  onCategory,
  onSeverity,
  onStatus,
  onClear,
}) {
  const hasFilters =
    partnerId || region || category || severity || status;
  return (
    <section
      data-testid="ph15-filter-bar"
      className="rounded-2xl bg-white border border-slate-200 p-4 mt-4"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
          <Filter className="w-3.5 h-3.5 text-violet-700" /> Filters
        </div>
        {hasFilters && (
          <button
            data-testid="ph15-filters-clear"
            onClick={onClear}
            className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
          >
            Clear all
          </button>
        )}
      </div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
        <select
          data-testid="ph15-filter-partner"
          value={partnerId || ""}
          onChange={(e) => onPartnerId(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All partners</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          data-testid="ph15-filter-region"
          value={region || ""}
          onChange={(e) => onRegion(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All regions</option>
          {regions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          data-testid="ph15-filter-category"
          value={category || ""}
          onChange={(e) => onCategory(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.slug} value={c.slug}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          data-testid="ph15-filter-severity"
          value={severity || ""}
          onChange={(e) => onSeverity(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="ph15-filter-status"
          value={status || ""}
          onChange={(e) => onStatus(e.target.value || null)}
          className="text-xs border border-slate-200 rounded-md px-2 py-1.5 bg-white"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {prettyStatus(s)}
            </option>
          ))}
        </select>
      </div>
    </section>
  );
}


// ── Follow-Up Action Queue ─────────────────────────────────────────────
function FollowUpQueue({ data, busyId, onStatusChange }) {
  const rows = data?.rows || [];
  return (
    <section
      data-testid="ph15-queue"
      className="rounded-2xl bg-white border border-slate-200 p-5 mt-4"
    >
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ListChecks className="w-4 h-4 text-violet-700" />
          <h3 className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Follow-Up Action Queue
          </h3>
        </div>
        <div className="text-xs text-slate-500">
          {data?.showing ?? rows.length} / {data?.count ?? rows.length}
        </div>
      </div>
      {rows.length === 0 ? (
        <div
          data-testid="ph15-queue-empty"
          className="text-xs text-slate-400 italic py-6 text-center"
        >
          No follow-up actions match these filters.
        </div>
      ) : (
        <div className="overflow-auto -mx-2">
          <table className="w-full text-xs" data-testid="ph15-queue-table">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left p-2">Booking</th>
                <th className="text-left p-2">Customer</th>
                <th className="text-left p-2">Partner</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">Recommended action</th>
                <th className="text-left p-2">Severity</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Updated</th>
                <th className="text-right p-2">Links</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.follow_up_id}
                  data-testid={`ph15-row-${r.follow_up_id}`}
                  className="border-t border-slate-100 align-top"
                >
                  <td className="p-2">
                    <Link
                      to={`/admin/bookings/${r.booking_reference}`}
                      data-testid={`ph15-row-booking-link-${r.follow_up_id}`}
                      className="text-violet-700 hover:underline font-mono"
                    >
                      {r.booking_reference}
                    </Link>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {fmtDate(r.published_at)}
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="text-slate-800">{r.customer_name || "—"}</div>
                    <div className="text-[10px] text-slate-500">
                      {r.customer_suburb || ""} {r.customer_postcode || ""}
                    </div>
                  </td>
                  <td className="p-2">
                    <div className="font-semibold text-slate-800">
                      {r.partner_business_name || (
                        <span className="text-slate-400 italic">(unknown)</span>
                      )}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">
                      {r.partner_slug || r.partner_id}
                    </div>
                    {r.partner_franchise_region && (
                      <div className="text-[10px] text-slate-500">
                        {r.partner_franchise_region}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <div className="font-semibold text-slate-800">
                      {r.recommendation?.label || r.category_slug}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      {r.main_finding}
                    </div>
                  </td>
                  <td className="p-2 max-w-[300px]">
                    <div className="text-slate-700">
                      {r.recommendation?.recommended_action}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 italic">
                      {r.recommendation?.next_step}
                    </div>
                    {Array.isArray(r.recommendation?.service_categories) &&
                      r.recommendation.service_categories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {r.recommendation.service_categories.map((sc) => (
                            <span
                              key={sc}
                              data-testid={`ph15-row-svc-${r.follow_up_id}-${sc}`}
                              className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 border border-violet-100 text-violet-700"
                            >
                              {sc.replaceAll("_", " ")}
                            </span>
                          ))}
                        </div>
                    )}
                    {r.recommendation?.biocleanse_note && (
                      <div
                        data-testid={`ph15-row-biocleanse-${r.follow_up_id}`}
                        className="text-[10px] text-violet-700 italic mt-1.5"
                      >
                        {r.recommendation.biocleanse_note}
                      </div>
                    )}
                  </td>
                  <td className="p-2">
                    <span
                      data-testid={`ph15-row-sev-${r.follow_up_id}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                        SEVERITY_TONES[r.recommendation?.severity] || ""
                      }`}
                    >
                      {r.recommendation?.severity || "—"}
                    </span>
                  </td>
                  <td className="p-2">
                    <select
                      value={r.status}
                      disabled={busyId === r.follow_up_id}
                      onChange={(e) => onStatusChange(r, e.target.value)}
                      data-testid={`ph15-row-status-select-${r.follow_up_id}`}
                      className={`text-[11px] border border-slate-200 rounded-md px-1.5 py-0.5 ${
                        STATUS_TONES[r.status] || ""
                      }`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {prettyStatus(s)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2 text-[11px]">
                    <div>{fmtDate(r.status_updated_at)}</div>
                    {r.status_updated_by && (
                      <div className="text-[10px] text-slate-500">
                        {r.status_updated_by}
                      </div>
                    )}
                  </td>
                  <td className="p-2 text-right">
                    {r.passport_test_id && (
                      <Link
                        to={`/admin/lab/tests/${r.passport_test_id}`}
                        data-testid={`ph15-row-passport-link-${r.follow_up_id}`}
                        className="text-[11px] text-violet-700 hover:underline inline-flex items-center gap-1"
                      >
                        Water Record <ExternalLink className="w-3 h-3" />
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p
        data-testid="ph15-queue-disclaimer"
        className="mt-3 text-[10px] text-slate-400 italic"
      >
        {data?.disclaimer ||
          "Recommended follow-up actions are calm, practical review suggestions only — not quotes."}
      </p>
    </section>
  );
}


// ── Page ────────────────────────────────────────────────────────────────
export default function AdminPWSFollowUps() {
  const [windowSel, setWindowSel] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [catalog, setCatalog] = useState({ catalog: [], statuses: [], severities: [] });
  const [allRows, setAllRows] = useState({ rows: [], count: 0, showing: 0 });
  const [partnerId, setPartnerId] = useState(null);
  const [region, setRegion] = useState(null);
  const [category, setCategory] = useState(null);
  const [severity, setSeverity] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Reload everything when window / partner / category / severity / status change.
  // Region is applied client-side so we only refetch on server-aware fields.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [s, c, r] = await Promise.all([
          api.get("/admin/pws-follow-ups/summary", { params: { window: windowSel } }),
          api.get("/admin/pws-follow-ups/catalog"),
          api.get("/admin/pws-follow-ups/recommendations", {
            params: {
              window: windowSel,
              ...(partnerId ? { partner_id: partnerId } : {}),
              ...(category ? { category } : {}),
              ...(severity ? { severity } : {}),
              ...(statusFilter ? { status: statusFilter } : {}),
              limit: 500,
            },
          }),
        ]);
        if (!alive) return;
        setSummary(s.data);
        setCatalog(c.data);
        setAllRows(r.data);
      } catch (e) {
        if (alive) setError(formatApiError(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [windowSel, partnerId, category, severity, statusFilter]);

  // Compute distinct partner + region lists from the current dataset.
  const partners = useMemo(() => {
    const map = new Map();
    (allRows.rows || []).forEach((r) => {
      if (r.partner_id && !map.has(r.partner_id)) {
        map.set(r.partner_id, {
          id: r.partner_id,
          label: r.partner_business_name || r.partner_slug || r.partner_id,
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [allRows]);

  const regions = useMemo(() => {
    const set = new Set();
    (allRows.rows || []).forEach((r) => {
      if (r.partner_franchise_region) set.add(r.partner_franchise_region);
    });
    return [...set].sort();
  }, [allRows]);

  const filteredRows = useMemo(() => {
    if (!region) return allRows.rows || [];
    return (allRows.rows || []).filter(
      (r) => r.partner_franchise_region === region,
    );
  }, [allRows, region]);

  const queueData = useMemo(
    () => ({
      ...allRows,
      rows: filteredRows,
      count: filteredRows.length,
      showing: filteredRows.length,
    }),
    [allRows, filteredRows],
  );

  const handleStatusChange = async (row, newStatus) => {
    setBusyId(row.follow_up_id);
    try {
      const { data } = await api.patch(
        `/admin/pws-follow-ups/${encodeURIComponent(
          row.booking_reference,
        )}/${encodeURIComponent(row.category_slug)}/status`,
        { status: newStatus, note: null, internal_note: null },
      );
      setAllRows((cur) =>
        cur
          ? {
              ...cur,
              rows: cur.rows.map((r) =>
                r.follow_up_id === row.follow_up_id
                  ? {
                      ...r,
                      status: newStatus,
                      status_updated_at:
                        data?.row?.updated_at || r.status_updated_at,
                      status_updated_by:
                        data?.row?.updated_by || r.status_updated_by,
                      audit_log: data?.row?.audit_log || r.audit_log,
                    }
                  : r,
              ),
            }
          : cur,
      );
      toast.success(`Status → ${prettyStatus(newStatus)}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Status update failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, c, r] = await Promise.all([
        api.get("/admin/pws-follow-ups/summary", { params: { window: windowSel } }),
        api.get("/admin/pws-follow-ups/catalog"),
        api.get("/admin/pws-follow-ups/recommendations", {
          params: {
            window: windowSel,
            ...(partnerId ? { partner_id: partnerId } : {}),
            ...(category ? { category } : {}),
            ...(severity ? { severity } : {}),
            ...(statusFilter ? { status: statusFilter } : {}),
            limit: 500,
          },
        }),
      ]);
      setSummary(s.data);
      setCatalog(c.data);
      setAllRows(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setPartnerId(null);
    setRegion(null);
    setCategory(null);
    setSeverity(null);
    setStatusFilter(null);
  };

  return (
    <PortalShell>
      <div
        data-testid="admin-pws-follow-ups-page"
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      >
        <Link
          to="/admin/partners/network"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-700 hover:text-violet-800"
          data-testid="ph15-back-to-network"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to PWS Network
        </Link>

        <div className="mt-3 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-violet-600">
              Aware Partner Network · Phase 15
            </div>
            <h1 className="font-display mt-2 text-3xl font-bold text-slate-900">
              Recommended Follow-Up Actions
            </h1>
            <p className="text-slate-500 mt-1 text-sm max-w-3xl">
              Calm, practical follow-up recommendations derived from every
              published partner-attributed result. Surfaces the booking,
              opportunity category, recommended action and severity, with
              admin-only audit-logged status updates.{" "}
              <strong className="text-slate-700">
                Recommendations only — no automated emails, no work orders, no
                quotes are sent from this page.
              </strong>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <WindowSelector window={windowSel} onChange={setWindowSel} />
            <button
              onClick={handleRefresh}
              disabled={loading}
              data-testid="ph15-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:opacity-60"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>

        {/* Safety banner — careful wording */}
        <div
          data-testid="ph15-safety-banner"
          className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3 text-sm text-amber-900"
        >
          <ShieldAlert className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Recommendation-only:</strong> nothing on this page sends
            emails, dispatches SMS, creates ServiceM8 work orders, generates
            quotes, or notifies partners or customers automatically. Status
            changes are admin-only and audit logged. Use calm wording —
            &ldquo;recommended review&rdquo;, &ldquo;potential
            follow-up&rdquo;, &ldquo;requires review against ADWG
            guidance&rdquo;. Avoid medical claims. BioCleanse is supplementary
            only.
          </div>
        </div>

        <StatStrip summary={summary} />

        <FilterBar
          partners={partners}
          regions={regions}
          categories={catalog?.catalog || []}
          partnerId={partnerId}
          region={region}
          category={category}
          severity={severity}
          status={statusFilter}
          onPartnerId={setPartnerId}
          onRegion={setRegion}
          onCategory={setCategory}
          onSeverity={setSeverity}
          onStatus={setStatusFilter}
          onClear={handleClearFilters}
        />

        {loading ? (
          <div
            data-testid="ph15-loading"
            className="mt-8 rounded-2xl bg-white border border-slate-200 p-10 flex items-center gap-2 text-slate-400 justify-center"
          >
            <Loader2 className="w-5 h-5 animate-spin" /> Loading follow-ups…
          </div>
        ) : error ? (
          <div
            data-testid="ph15-error"
            className="mt-8 rounded-xl bg-rose-50 border border-rose-200 p-5 text-sm text-rose-800"
          >
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Failed to load
            </div>
            <div className="text-xs">{error}</div>
          </div>
        ) : (
          <FollowUpQueue
            data={queueData}
            busyId={busyId}
            onStatusChange={handleStatusChange}
          />
        )}
      </div>
    </PortalShell>
  );
}
